import React from "react";
import { getSolfaName, i18nSolfa, lengthenSolfa, localizeAbc, parseWithPitchNotation } from "../../../../../common/parameters";
import { IDoPos, TonesetText } from "../../../../../common/types";
import { PitchNotation } from "../../../../store/user/models";
import handsigns from "../../../../icons/handsigns";
import { useUser } from "../../../../store/user/selectors";
import { getAbcName } from "../../../Score/score-display/Note/Texts/AbcName/utils";
import { IMelodyTableNote } from "../MelodyTable/MelodyTableBar/interfaces";
import { IDropParamsProps, IPosition } from "./interfaces";
import { HandsignWrapper } from "./styles";

const margin = 10;
const isOverlaps = (drops: IPosition[], drag: IPosition, width: number, height: number) => {
    for (let d = 0; d < drops.length; d++) {
        const rect1 = {
            left: drops[d].x - margin - width / 2,
            right: drops[d].x + width * 1.5 + margin * 2,
            top: drops[d].y - height / 2 - margin,
            bottom: drops[d].y + height * 1.5 + margin * 2,
        };
        const rect2 = {
            left: drag.x - margin - width / 2,
            right: drag.x + width * 1.5 + margin * 2,
            top: drag.y - height / 2 - margin,
            bottom: drag.y + height * 1.5 + margin * 2,
        };
        const x_overlap = Math.max(0, Math.min(rect1.right, rect2.right) - Math.max(rect1.left, rect2.left));
        const y_overlap = Math.max(0, Math.min(rect1.bottom, rect2.bottom) - Math.max(rect1.top, rect2.top));
        const overlapArea = x_overlap * y_overlap;
        if (overlapArea > 0) return true;
    }
    return false;
};

export const getRandomDragPositions = ({ drops, width, height }: IDropParamsProps, size: IPosition, lowDim: number) => {
    let totalTries = 0;
    const minX = drops.sort((drop1, drop2) => drop1.x - drop2.x)[0].x;
    const minY = Math.min(
        size.y / -2,
        drops.sort((drop1, drop2) => drop1.y - drop2.y)[0].y
    ) * lowDim;
    const maxX = drops.sort((drop1, drop2) => drop2.x - drop1.x)[0].x;
    const maxY = Math.max(
        size.y * 4,
        drops.sort((drop1, drop2) => drop2.y - drop1.y)[0].y
    ) * lowDim;

    let drags = [];
    let d = 0;
    while (d < drops.length) {
        let x;
        let y;
        let aborted = false;
        do {
            x = minX + Math.random() * (maxX - minX);
            y = minY + Math.random() * (maxY - minY);
            totalTries++;
            if (totalTries > 500) {
                d = 0;
                totalTries = 0;
                aborted = true;
                drags = [];
                break;
            }
        } while (isOverlaps([...drops, ...drags], { x, y }, width * lowDim, height * lowDim))
        if (!aborted) {
            drags.push({ x, y });
            d++;
        }
    }
    return drags;
}

export const Text = ({
    data: {
        note,
        locale,
        method,
        doPos,
        colors,
        size,
        className,
        color,
        lowDim,
        octave0 = 0,
    } }: {
        data: {
            note: IMelodyTableNote,
            locale: string,
            method: TonesetText,
            doPos: IDoPos,
            colors: boolean,
            size: { y: number },
            className: string,
            color: string,
            lowDim: number,
            octave0: number,
        }
    }): JSX.Element => {
    const { pitchNotation = PitchNotation.Helmholtz, solfaShort = false, solfaKam } = useUser()
    switch (method) {
        case TonesetText.Solfa:
            return <>{lengthenSolfa(i18nSolfa(getSolfaName(note.pitch, locale), locale, solfaKam), locale, solfaShort)}</>;
        case TonesetText.Abc:
            const abc = parseWithPitchNotation(localizeAbc(getAbcName(note.pitch, doPos, octave0), locale), pitchNotation);
            return <div style={{ ...(abc.length > 5 && { letterSpacing: -1 }) }}>{abc}</div>;
        case TonesetText.Handsign:
            const Handsign = handsigns[note.pitch.s][note.pitch.a || 0] as () => JSX.Element;
            return (
                <HandsignWrapper
                    className={className}
                    solfaShort={colors && note.pitch && getSolfaName(note.pitch, "hu").slice(0, 1)}
                    coloring={colors}
                    lineDistance={size.y * lowDim}
                    color={color}
                >
                    <Handsign />
                </HandsignWrapper>
            )
        default:
            return <></>;
    }
}